import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Alert,
  CircularProgress,
} from '@mui/material';
// Box import removed for Material-UI v7 compatibility
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  ShoppingCart as ShoppingCartIcon,
  AttachMoney as AttachMoneyIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import AdminLayout from '../../components/layout/AdminLayout';

interface AnalyticsData {
  totalRevenue: number;
  totalOrders: number;
  totalCustomers: number;
  averageOrderValue: number;
  revenueGrowth: number;
  orderGrowth: number;
  customerGrowth: number;
  topProducts: TopProduct[];
  revenueByMonth: RevenueData[];
  categoryPerformance: CategoryData[];
}

interface TopProduct {
  id: number;
  name: string;
  sales: number;
  revenue: number;
  growth: number;
}

interface RevenueData {
  month: string;
  revenue: number;
  orders: number;
}

interface CategoryData {
  category: string;
  sales: number;
  revenue: number;
  percentage: number;
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  growth?: number;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, growth }) => (
  <Card
    sx={{
      height: '100%',
      background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
      border: `1px solid ${color}20`,
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 25px ${color}25`,
      },
    }}
  >
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: '#666',
              fontWeight: 500,
              mb: 1,
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#333',
              mb: growth !== undefined ? 0.5 : 0,
            }}
          >
            {typeof value === 'number' ? value.toLocaleString() : value}
          </Typography>
          {growth !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {growth > 0 ? (
                <TrendingUpIcon sx={{ color: '#4CAF50', fontSize: 16 }} />
              ) : (
                <TrendingDownIcon sx={{ color: '#F44336', fontSize: 16 }} />
              )}
              <Typography
                variant="body2"
                sx={{
                  color: growth > 0 ? '#4CAF50' : '#F44336',
                  fontWeight: 600,
                }}
              >
                {Math.abs(growth)}%
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: `${color}20`,
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {React.cloneElement(icon as React.ReactElement<any>, {
            style: { color: color, fontSize: 28 },
          })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const AdminAnalytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30');

  // Mock data for demonstration
  useEffect(() => {
    const mockAnalytics: AnalyticsData = {
      totalRevenue: 15840000,
      totalOrders: 324,
      totalCustomers: 158,
      averageOrderValue: 48900,
      revenueGrowth: 12.5,
      orderGrowth: 8.3,
      customerGrowth: 15.2,
      topProducts: [
        {
          id: 1,
          name: 'Premium Foam Cleanser',
          sales: 245,
          revenue: 7105000,
          growth: 18.5,
        },
        {
          id: 2,
          name: 'Anti-Aging Serum',
          sales: 156,
          revenue: 7020000,
          growth: 15.2,
        },
        {
          id: 3,
          name: 'Moisturizing Toner',
          sales: 189,
          revenue: 4725000,
          growth: -3.1,
        },
      ],
      revenueByMonth: [
        { month: 'Jan', revenue: 3200000, orders: 65 },
        { month: 'Feb', revenue: 2800000, orders: 58 },
        { month: 'Mar', revenue: 3600000, orders: 72 },
        { month: 'Apr', revenue: 4100000, orders: 84 },
        { month: 'May', revenue: 4800000, orders: 98 },
        { month: 'Jun', revenue: 5200000, orders: 108 },
      ],
      categoryPerformance: [
        { category: 'Cleanser', sales: 245, revenue: 7105000, percentage: 44.8 },
        { category: 'Serum', sales: 156, revenue: 7020000, percentage: 44.3 },
        { category: 'Toner', sales: 189, revenue: 4725000, percentage: 29.8 },
        { category: 'Cream', sales: 98, revenue: 2940000, percentage: 18.6 },
      ],
    };

    setTimeout(() => {
      setAnalytics(mockAnalytics);
      setLoading(false);
    }, 1000);
  }, [timeRange]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW',
    }).format(amount);
  };

  if (loading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} sx={{ color: '#35C5F0' }} />
        </Box>
      </AdminLayout>
    );
  }

  if (!analytics) {
    return (
      <AdminLayout>
        <Alert severity="error" sx={{ mb: 3 }}>
          Unable to load analytics data.
        </Alert>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#333',
            }}
          >
            Analytics
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={timeRange}
              label="Period"
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <MenuItem value="7">7 Days</MenuItem>
              <MenuItem value="30">30 Days</MenuItem>
              <MenuItem value="90">90 Days</MenuItem>
              <MenuItem value="365">1 Year</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Typography variant="body1" color="textSecondary">
          Analyze business performance and trends
        </Typography>
      </Box>

      {/* Key Metrics */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3, mb: 4 }}>
        <Box>
          <StatCard
            title="Total Revenue"
            value={formatCurrency(analytics.totalRevenue)}
            icon={<AttachMoneyIcon />}
            color="#35C5F0"
            growth={analytics.revenueGrowth}
          />
        </Box>
        <Box>
          <StatCard
            title="Total Orders"
            value={analytics.totalOrders}
            icon={<ShoppingCartIcon />}
            color="#4CAF50"
            growth={analytics.orderGrowth}
          />
        </Box>
        <Box>
          <StatCard
            title="Total Customers"
            value={analytics.totalCustomers}
            icon={<PeopleIcon />}
            color="#FF7F2F"
            growth={analytics.customerGrowth}
          />
        </Box>
        <Box>
          <StatCard
            title="Average Order Value"
            value={formatCurrency(analytics.averageOrderValue)}
            icon={<InventoryIcon />}
            color="#9C27B0"
          />
        </Box>
      </Box>

      {/* Dashboard Content Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 4 }}>
        {/* Top Products */}
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Top Products
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {analytics.topProducts.map((product, index) => (
                <Box
                  key={product.id}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f8f9fa',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        backgroundColor: '#35C5F0',
                        color: 'white',
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                      }}
                    >
                      {index + 1}
                    </Typography>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {product.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {product.sales} sold
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatCurrency(product.revenue)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {product.growth > 0 ? (
                        <TrendingUpIcon sx={{ color: '#4CAF50', fontSize: 16 }} />
                      ) : (
                        <TrendingDownIcon sx={{ color: '#F44336', fontSize: 16 }} />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          color: product.growth > 0 ? '#4CAF50' : '#F44336',
                          fontWeight: 600,
                        }}
                      >
                        {Math.abs(product.growth)}%
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Revenue by Month */}
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Monthly Revenue
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Month</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Revenue</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Orders</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics.revenueByMonth.map((data) => (
                    <TableRow key={data.month}>
                      <TableCell>{data.month}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {formatCurrency(data.revenue)}
                      </TableCell>
                      <TableCell align="right">
                        {data.orders}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      {/* Category Performance - Full Width */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Category Performance
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            {analytics.categoryPerformance.map((category, index) => (
              <Box
                key={category.category}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: '#f8f9fa',
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#35C5F0',
                    mb: 1,
                  }}
                >
                  {category.category}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {category.sales} sold
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, mb: 2 }}
                >
                  {formatCurrency(category.revenue)}
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={category.percentage}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#35C5F0',
                      },
                    }}
                  />
                </Box>
                <Typography variant="body2" color="textSecondary">
                  {category.percentage}%
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default AdminAnalytics;